import React, { useCallback, useMemo, useState } from 'react';
import Stack from '@mui/material/Stack';
import ApiService from '@common/services/ApiService';
import { DefaultModal } from '@common/components/default-modal';
import { DefaultButton } from '@common/components/default-button';
import { orderBy, difference, union, groupBy } from 'lodash';
import CourseUnits from './CourseUnits';
import { format } from 'date-fns';
import { Typography } from '@mui/material';
const SINGLE_UNITS_COURSE_NAME = 'Single Units';

const api = new ApiService();
const VcmDownloadModal = ({
  isOpened,
  onClose,
  vcm,
  unitResponses,
  courses
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [excludedUnitIds, setExcludedUnitIds] = useState([]);

  const coursesUnits = useMemo(() => {
    if (!unitResponses.length) {
      return [];
    }

    return orderBy(
      unitResponses
        .map((unitResponse) => {
          if (unitResponse.owner_course_ids.length) {
            return courses
              .filter((course) =>
                unitResponse.owner_course_ids.includes(course.id)
              )
              .map((course) => ({
                unit: unitResponse.unit,
                courseName: `${course.code} - ${course.title}`
              }));
          }
          return [
            { unit: unitResponse.unit, courseName: SINGLE_UNITS_COURSE_NAME }
          ];
        })
        .flat(),
      ['courseName', 'unit.code', 'unit.title']
    );
  }, [courses, unitResponses]);

  const selectedUnitIds = useMemo(() => {
    if (excludedUnitIds.length === 0) {
      return null;
    }

    return difference(
      unitResponses.map(({ unit }) => unit.id),
      excludedUnitIds
    );
  }, [excludedUnitIds, unitResponses]);

  const courseGroups = useMemo(
    () => groupBy(coursesUnits, 'courseName'),
    [coursesUnits]
  );

  const handleToggleSelection = useCallback((ids, exclude) => {
    setExcludedUnitIds((previouslyExcluded) => {
      return exclude
        ? union(previouslyExcluded, ids)
        : difference(previouslyExcluded, ids);
    });
  }, []);

  const handleClose = useCallback(() => {
    setErrorMessage(null);
    onClose();
  }, [setErrorMessage, onClose]);

  const handleDownload = useCallback(async () => {
    setIsLoading(true);
    try {
      let searchRequest = '';
      if (selectedUnitIds) {
        searchRequest = api.searchUrl(null, { unit_ids: selectedUnitIds });
      }

      const userFileName = vcm.owner_user.full_name
        ? `${vcm.owner_user.full_name}_`.replace(/[^a-z0-9\-_]/gi, '')
        : '';
      const filename = `${userFileName}vcm_${vcm.revision_text}_${format(
        Date.now(),
        'yyyy-MM-dd'
      )}.pdf`;

      const res = await api.getBlob(
        'vcms',
        vcm.id + '.pdf' + (searchRequest !== '' ? '?' : '') + searchRequest
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      handleClose();
    } catch (error) {
      console.log(error);
      setErrorMessage(error.message);
    } finally {
      setIsLoading(false);
    }
  }, [selectedUnitIds, vcm]);

  return (
    <DefaultModal
      wide
      isOpened={isOpened}
      onClose={handleClose}
      title="Download VCM"
      action={
        <Stack sx={{ pt: 2, alignItems: 'flex-start' }}>
          <DefaultButton
            color="secondary"
            loading={isLoading}
            onClick={handleDownload}
            size="small"
            variant="contained"
            disabled={selectedUnitIds?.length === 0}
          >
            Download PDF
          </DefaultButton>
        </Stack>
      }
    >
      <Stack sx={{ gap: 2 }}>
        {errorMessage ? (
          <Typography color="error">{errorMessage}</Typography>
        ) : null}
        <Stack
          sx={{ height: 400, borderRadius: '8px', border: '1px solid #ccc' }}
        >
          <Stack
            sx={{
              gap: '20px',
              overflowY: 'scroll',
              p: 1
            }}
          >
            {Object.keys(courseGroups).map((courseName) => (
              <CourseUnits
                key={courseName}
                courseName={courseName}
                courseUnits={courseGroups[courseName]}
                excludedUnitIds={excludedUnitIds}
                onToggleSelection={handleToggleSelection}
              />
            ))}
          </Stack>
        </Stack>
      </Stack>
    </DefaultModal>
  );
};

export default VcmDownloadModal;
